import Phaser from 'phaser';
import { SCENE_KEYS, ZONE_NAMES } from '../data/gameConstants';
import { Collectible } from '../objects/Collectible';
import { InteractionZone } from '../objects/InteractionZone';
import { NPC } from '../objects/NPC';
import { SaveSystem } from '../systems/SaveSystem';
import { ZoneCoreSystems } from '../systems/ZoneCoreSystems';
import { ObjectiveHint } from '../ui/ObjectiveHint';
import { QuestPanel } from '../ui/QuestPanel';
import { addZoneHudHeader } from '../ui/ZoneHudHeader';
import { addZoneMenuButton } from '../ui/zoneMenuButton';
import { SceneTransition } from '../utils/SceneTransition';
import { Zone1Map, ZONE1_POSITIONS } from '../zones/zone1/Zone1Map';
import { Zone1QuestFlow } from '../zones/zone1/Zone1QuestFlow';

export class Zone1SkyMessageScene extends Phaser.Scene {
  private core?: ZoneCoreSystems;
  private questPanel?: QuestPanel;
  private questFlow?: Zone1QuestFlow;
  private map?: Zone1Map;
  private rafflesia?: Collectible;
  private objectiveHint?: ObjectiveHint;

  constructor() {
    super({ key: 'Zone1SkyMessageScene' });
  }

  create(): void {
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);

    this.map = new Zone1Map(this);
    const obstacles = this.map.build();

    this.core = new ZoneCoreSystems(this);
    this.questPanel = new QuestPanel(this);
    this.objectiveHint = new ObjectiveHint(this);
    this.core.setQuestPanel(this.questPanel);
    this.core.setupHUD(1);
    this.core.setupMobileControls();
    const startPos = SaveSystem.getPlayerStartPosition(
      SCENE_KEYS.ZONE1,
      ZONE1_POSITIONS.SPAWN.x,
      ZONE1_POSITIONS.SPAWN.y,
    );
    SaveSystem.saveProgress(SCENE_KEYS.ZONE1, 1, startPos);
    this.core.initPlayer(startPos.x, startPos.y);
    this.core.setupObstacles(obstacles);

    const kakek = new NPC(this, ZONE1_POSITIONS.KAKEK.x, ZONE1_POSITIONS.KAKEK.y, {
      id: 'npc_kakek_sakti',
      name: 'Kakek Sakti',
      type: 'kakek_sakti',
      interactionRadius: 100,
    });

    const signZone = new InteractionZone(
      this,
      ZONE1_POSITIONS.SIGN.x,
      ZONE1_POSITIONS.SIGN.y,
      80,
      70,
      {
        id: 'zone1_sign',
        type: 'custom',
        label: 'Baca Papan Petunjuk',
        interactionRadius: 85,
      },
    );

    this.rafflesia = new Collectible(this, ZONE1_POSITIONS.RAFFLESIA.x, ZONE1_POSITIONS.RAFFLESIA.y, {
      id: 'zone1_rafflesia_hint',
      label: 'Rafflesia Point',
      type: 'rafflesia',
      scoreRuleId: 'zone1_hint',
      dialogueId: 'zone1_rafflesia',
      startHidden: true,
      interactionRadius: 75,
    });

    this.questFlow = new Zone1QuestFlow(
      this.core,
      this.map,
      kakek,
      this.rafflesia,
      this.objectiveHint,
    );

    signZone.setOnInteract(() => this.questFlow!.onSignInteract());
    kakek.setOnInteract(() => this.questFlow!.onKakekInteract());
    this.rafflesia.setOnCollect(() => this.questFlow!.onRafflesiaCollected());

    this.core.registerInteractables([signZone, kakek, this.rafflesia]);
    this.buildUI();
    this.questFlow.start();
    SceneTransition.reveal(this);
  }

  private buildUI(): void {
    addZoneHudHeader(this, ZONE_NAMES.ZONE1, 'Kerajaan Peremban Panas — Bumi Raflesia');
    addZoneMenuButton(this);
  }

  update(time: number, delta: number): void {
    this.core?.update(time, delta);
    this.rafflesia?.updateGlow();

    const player = this.core?.getPlayer();
    if (player && this.questFlow) {
      this.questFlow.checkForestExit(player.x);
      this.questFlow.checkForestBlocked(player.x);
    }
  }

  shutdown(): void {
    this.questFlow?.destroy();
    this.core?.destroy();
    this.questFlow = undefined;
    this.core = undefined;
    this.questPanel = undefined;
  }
}
